import { useState, useEffect, useRef } from "react"
import { Range } from 'react-date-range'
import styles from "./CustomRangeFilter.module.css"
import AddTraspButton from "../../components/firstTimeUserExperience/commonCom/AddTraspButton";
import SimpleButton from "../buttons/Simple-button";
import CalenderIcon from "../../assets/svgs/calenderIcon";
import SingleDropdown from "../singleDropdown/SingleDropdown";
import Chevren_downIcon from "../../assets/svgs/Chevren_down";

interface CustomRangeFilterProps {
  onApplyFilter?: (filterType: string, selectedDates?: { startDate: Date | null; endDate: Date | null }) => void;
  range?: Range[];
  customRanges?: { label: string; range: () => { startDate: Date; endDate: Date } }[];
  onChangeHandler?: (ranges: Range[]) => void;
  relativeDateString?: string;
  setRelativeDateString?: (label: string) => void;
  fetchData?: () => void;
  applyButton?: boolean;
  fetchData2?: (value: any) => void;
  ClearFilter?: () => void;
  setShow?: (value: boolean) => void;
  selectedView?: string;
}

const FILTER_OPTIONS = ["Last 30 Days", "Last 3 Months", "Last 6 Months", "Last Year", "Custom"]

const DAYS_OF_WEEK = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"]

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

export default function CustomRangeFilter({
  onApplyFilter,
  onChangeHandler,
  relativeDateString: propRelativeDateString,
  setRelativeDateString: propSetRelativeDateString,
  customRanges,
  range: propRange,
  fetchData,
  fetchData2,
  ClearFilter,
  setShow,
  selectedView,
  applyButton = true
}: CustomRangeFilterProps) {
  // Must match what the mount effect below actually applies (Last 30 Days) --
  // this previously said "Last Year", so the button showed the wrong label for
  // whatever data was really being fetched on first load.
  const [selectedFilter, setSelectedFilter] = useState("Last 30 Days")
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth())
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear())
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [isDateSelected, setIsDateSelected] = useState(false)
  const [calendarPosition, setCalendarPosition] = useState<{
    left?: string
    right?: string
    top?: string
    bottom?: string
    transform?: string
    marginTop?: string
    marginBottom?: string
  }>({})
  const buttonRef = useRef<HTMLButtonElement>(null)
  const calendarRef = useRef<HTMLDivElement>(null)


  // Initialize with Last 30 Days and Trigger Parent
  useEffect(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const startDateObj = new Date(today)
    startDateObj.setDate(today.getDate() - 29)


    setStartDate(startDateObj)
    setEndDate(today)
    setCurrentMonth(today.getMonth())
    setCurrentYear(today.getFullYear())
    setIsDateSelected(true)


    if (onChangeHandler) {
      onChangeHandler([{
        startDate: startDateObj,
        endDate: today,
        key: 'selection'
      }])
    }



    if (selectedView === "Grouped View" && fetchData) {
      fetchData()
    }


    if (onApplyFilter) {
      onApplyFilter("Last 30 Days", { startDate: startDateObj, endDate: today })
    }

  }, [])

  // Sync with external relativeDateString
  useEffect(() => {
    if (propRelativeDateString && customRanges) {
      const matchedRange = customRanges.find(range => range.label === propRelativeDateString)
      if (matchedRange) {
        setIsDateSelected(true)
        setSelectedFilter(propRelativeDateString)
        const rangeData = matchedRange.range()
        setStartDate(rangeData.startDate)
        setEndDate(rangeData.endDate)
      }
    }
  }, [propRelativeDateString, customRanges])

  // Sync with external range prop
  useEffect(() => {
    if (propRange && propRange[0] && propRange[0].startDate && propRange[0].endDate) {
      setStartDate(propRange[0].startDate)
      setEndDate(propRange[0].endDate)
      setIsDateSelected(true)
    }
  }, [propRange])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsCalendarOpen(false)
      }
    }

    if (isCalendarOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isCalendarOpen])

  const calculatePresetRange = (filterType: string) => {
    const today = new Date()
    const startDateObj = new Date(today)
    const endDateObj = new Date(today)

    switch (filterType) {
      case "Last 30 Days":
        startDateObj.setDate(today.getDate() - 29)
        break
      case "Last 3 Months":
        startDateObj.setMonth(today.getMonth() - 3)
        break
      case "Last 6 Months":
        startDateObj.setMonth(today.getMonth() - 6)
        break
      case "Last Year":
        startDateObj.setFullYear(today.getFullYear() - 1)
        break
    }

    return { startDate: startDateObj, endDate: endDateObj }
  }

  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate()
  }

  const getFirstDayOfMonth = (month: number, year: number) => {
    const firstDay = new Date(year, month, 1).getDay()
    return firstDay === 0 ? 6 : firstDay - 1
  }

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11)
      setCurrentYear(currentYear - 1)
    } else {
      setCurrentMonth(currentMonth - 1)
    }
  }

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0)
      setCurrentYear(currentYear + 1)
    } else {
      setCurrentMonth(currentMonth + 1)
    }
  }

  const handlePrevYear = () => {
    setCurrentYear(currentYear - 1)
  }

  const handleNextYear = () => {
    setCurrentYear(currentYear + 1)
  }


  const handleDateClick = (date: number) => {
    const clickedDate = new Date(currentYear, currentMonth, date)

    // Force Custom Mode
    setSelectedFilter("Custom")

    // CASE 1: No start date OR both start & end already selected → reset and start new range
    if (!startDate || endDate) {
      setStartDate(clickedDate)
      setEndDate(null)

      onChangeHandler?.([
        { startDate: clickedDate, endDate: null, key: "selection" }
      ])

      return
    }

    // ------------------------------
    // CASE 2: Selecting second date
    // ------------------------------
    let finalStart = startDate
    let finalEnd = clickedDate

    if (clickedDate < startDate) {
      finalStart = clickedDate
      finalEnd = startDate
    }

    setStartDate(finalStart)
    setEndDate(finalEnd)

    onChangeHandler?.([
      { startDate: finalStart, endDate: finalEnd, key: "selection" }
    ])

    //  Run only when BOTH are selected
    if (finalStart && finalEnd) {
      propSetRelativeDateString?.("Custom")
    }
  }


  const isDateInRange = (date: number) => {
    if (!startDate) return false
    const currentDate = new Date(currentYear, currentMonth, date)

    if (!endDate) {
      return currentDate.getTime() === startDate.getTime()
    }

    return currentDate >= startDate && currentDate <= endDate
  }

  const isRangeEdge = (date: number) => {
    if (!startDate) return false
    const currentDate = new Date(currentYear, currentMonth, date)
    const isStart = currentDate.getTime() === startDate.getTime()
    const isEnd = endDate && currentDate.getTime() === endDate.getTime()
    return isStart || isEnd
  }

  const handleFilterClick = (filter: string) => {
    setSelectedFilter(filter)
    setIsDateSelected(true)

    if (filter !== "Custom") {
      const range = calculatePresetRange(filter)


      setCurrentMonth(range.endDate.getMonth())
      setCurrentYear(range.endDate.getFullYear())

      setStartDate(range.startDate)
      setEndDate(range.endDate)

      if (onChangeHandler) {
        onChangeHandler([{
          startDate: range.startDate,
          endDate: range.endDate,
          key: 'selection'
        }])
      }

      // Not relabeled to "Last Month" -- that maps to DateFilterTypesEnum.LastMonth,
      // which the backend computes as the entire previous calendar month, not a
      // rolling 30-day window. Passing "Last 30 Days" through as-is falls through
      // DateHelper.getDateFilterType's default case to DateFilterTypesEnum.Custom,
      // routing this preset through the same explicit-start/end-date mechanism the
      // real date-picker "Custom" flow already uses -- see buildDateParams in
      // newComponents/transactions/view.tsx.
      if (propSetRelativeDateString) {
        propSetRelativeDateString(filter)
      }
    } else {
      setSelectedFilter("Custom")

      if (propSetRelativeDateString) {
        propSetRelativeDateString("Custom")
      }
    }
  }

  const handleApplyFilter = () => {
    setIsCalendarOpen(false)

    // Call the appropriate fetch functions based on selectedView
    if (selectedView === "Grouped View") {
      fetchData?.()
    }
    fetchData2?.(1)

    onApplyFilter?.(selectedFilter, { startDate, endDate })
  }

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation()

    // Reset to Last 30 Days
    const today = new Date()
    const startDateObj = new Date(today)
    startDateObj.setDate(today.getDate() - 29)

    setSelectedFilter("Last 30 Days")
    setStartDate(startDateObj)
    setEndDate(today)
    setIsDateSelected(false)
    setIsCalendarOpen(false)

    if (propSetRelativeDateString) {
      propSetRelativeDateString("")
    }

    if (onChangeHandler) {
      onChangeHandler([{
        startDate: new Date(),
        endDate: new Date(),
        key: 'selection'
      }])
    }

    ClearFilter?.()
  }

  const toggleCalendar = () => {
    if (!isCalendarOpen) {
      const newPosition = calculateCalendarPosition()
      setCalendarPosition(newPosition)
    }
    setShow?.(false)
    setIsCalendarOpen(!isCalendarOpen)
  }

  const getDateRangeText = () => {
    if (!isDateSelected && !startDate && !endDate) {
      return "Duration"
    }

    if (selectedFilter !== "Custom" && selectedFilter) {
      return selectedFilter
    }

    if (startDate && endDate) {
      const formatDate = (date: Date) => {
        return date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      }
      return `${formatDate(startDate)} - ${formatDate(endDate)}`
    }

    if (startDate) {
      return `From ${startDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })}`
    }

    return "Duration"
  }

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear)
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear)
    const days = []


    const todayDate = new Date()
    const currentDay = todayDate.getDate()
    const currentMonthIndex = todayDate.getMonth()
    const currentYearVal = todayDate.getFullYear()

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className={styles.emptyDay}></div>)
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const isInRange = isDateInRange(day)
      const isEdge = isRangeEdge(day)

      // Check if this specific day is "Today"
      const isToday =
        day === currentDay &&
        currentMonth === currentMonthIndex &&
        currentYear === currentYearVal

      let dayClasses = styles.calendarDay
      if (isEdge) {
        dayClasses += ` ${styles.rangeEdge}`
      } else if (isInRange) {
        dayClasses += ` ${styles.selectedDay}`
      }

      days.push(
        <button key={day} className={dayClasses} onClick={() => handleDateClick(day)}>
          {day}

          {isToday && <span className={styles.todayDot}></span>}
        </button>,
      )
    }

    return days
  }

  const calculateCalendarPosition = () => {
    if (!buttonRef.current) return {}

    const buttonRect = buttonRef.current.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const calendarWidth = 800

    const position: any = {}

    const spaceOnRight = viewportWidth - buttonRect.right
    const spaceOnLeft = buttonRect.left

    if (spaceOnRight >= calendarWidth) {
      position.left = "0"
    } else if (spaceOnLeft >= calendarWidth) {
      position.right = "0"
    } else {
      position.left = "50%"
      position.transform = "translateX(-50%)"
    }

    position.top = "100%"
    position.marginTop = "8px"

    return position
  }

  return (
    <div className={styles.wrapper}>
      <button ref={buttonRef} className="time-range-button durationButton" onClick={toggleCalendar}>
        <CalenderIcon className="iconStyleWithWhiteFilter" />
        <span className={styles.buttonText}>{getDateRangeText()}</span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={`dropdown-arrow ${isCalendarOpen ? "dropdown-arrow-open" : ""}`}
        >
          <path d="M4 6L8 10L12 6" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {isCalendarOpen && (
        <div ref={calendarRef} className={styles.container} style={calendarPosition}>
          <div className={styles.mobileDropdownContainer}>
            <SingleDropdown
              triggerText={selectedFilter || "Filter By"}
              triggerIcon={<Chevren_downIcon width={"18"} height={"18"} />}
              triggerIconPosition="right"
              menuItems={FILTER_OPTIONS.map((option) => ({
                label: option,
                action: () => handleFilterClick(option),
              }))}
              align="left"
            />
          </div>
          <div className={styles.sidebar}>
            <h3 className={styles.filterTitle }>Filter By</h3>
            <div className={styles.filterOptions}>
              {FILTER_OPTIONS.map((option) => (
                <button
                  key={option}
                  className={`${styles.filterOption} ${selectedFilter === option ? styles.activeFilter : ""}`}
                  onClick={() => handleFilterClick(option)}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.calendarSection}>
            <div className={styles.calendarHeader}>
              <button className={styles.navButton} onClick={handlePrevMonth}>
                &#8249;
              </button>
              <div className={styles.dateDisplay}>
                <h3 className={styles.monthTitle}>{MONTHS[currentMonth]}</h3>
                <div className={styles.yearNavigation}>
                  <button className={styles.yearNavButton} onClick={handlePrevYear}>
                    &#8249;
                  </button>
                  <span className={styles.yearTitle}>{currentYear}</span>
                  <button className={styles.yearNavButton} onClick={handleNextYear}>
                    &#8250;
                  </button>
                </div>
              </div>
              <button className={styles.navButton} onClick={handleNextMonth}>
                &#8250;
              </button>
            </div>

            <div className={styles.daysOfWeek}>
              {DAYS_OF_WEEK.map((day) => (
                <div key={day} className={styles.dayOfWeek}>
                  {day}
                </div>
              ))}
            </div>

            <div className={styles.calendarGrid}>{renderCalendarDays()}</div>

            {applyButton && (
              <div className={styles.calendarFooter}>
                <AddTraspButton
                  bname="Clear filter"
                  onClick={handleClear}
                  width="fit-content"
                />
                <SimpleButton
                  label={"Apply filter"}
                  onClick={handleApplyFilter}
                />

              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}